/**
 * Content Swipe Page - Holo.ai-style Tinder-like content approval
 * Swipe right to approve, left to reject content
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/icons';
import { format } from 'date-fns';
import type { SocialPlatform } from '../types';

interface ContentItem {
  id: string;
  brand_id: string;
  title: string | null;
  content: string;
  platforms: SocialPlatform[];
  hashtags: string[];
  content_type: string;
  generation_batch_id: string | null;
  status: string;
  suggested_post_time: number | null;
  quality_score: number;
  engagement_prediction: number;
  tags: string[];
  created_at: number;
}

export function ContentSwipe() {
  const { selectedBrand } = useBrand();
  const navigate = useNavigate();
  const [items, setItems] = useState<ContentItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [stats, setStats] = useState({ approved: 0, rejected: 0, pending: 0 });
  const [useSuggestedTime, setUseSuggestedTime] = useState(true);

  // Generation settings
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateCount, setGenerateCount] = useState(10);
  const [generatePlatforms, setGeneratePlatforms] = useState<SocialPlatform[]>(['instagram']);
  const [generateThemes, setGenerateThemes] = useState('');
  const [useBrandDNA, setUseBrandDNA] = useState(true);

  useEffect(() => {
    if (selectedBrand) {
      fetchContent();
    }
  }, [selectedBrand]);

  const fetchContent = async () => {
    if (!selectedBrand) return;

    setIsLoading(true);
    try {
      const response = await apiClient.getContentPool({
        brand_id: selectedBrand.id,
        status: 'pending',
        limit: 100,
      });

      if (response.success && response.data) {
        setItems(response.data);
        setCurrentIndex(0);
        setStats(prev => ({ ...prev, pending: response.data?.length || 0 }));
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!items[currentIndex]) return;

    setSwipeDirection('right');
    setTimeout(async () => {
      try {
        await apiClient.updateContentPoolItem(items[currentIndex].id, { status: 'approved' });
        setStats(prev => ({ ...prev, approved: prev.approved + 1, pending: prev.pending - 1 }));
        goToNext();
      } catch (error) {
        console.error('Failed to approve:', error);
      }
      setSwipeDirection(null);
    }, 300);
  };

  const handleReject = async () => {
    if (!items[currentIndex]) return;

    setSwipeDirection('left');
    setTimeout(async () => {
      try {
        await apiClient.updateContentPoolItem(items[currentIndex].id, {
          status: 'rejected',
          rejection_reason: 'Rejected during swipe review',
        });
        setStats(prev => ({ ...prev, rejected: prev.rejected + 1, pending: prev.pending - 1 }));
        goToNext();
      } catch (error) {
        console.error('Failed to reject:', error);
      }
      setSwipeDirection(null);
    }, 300);
  };

  const handleSchedule = async () => {
    if (!items[currentIndex]) return;

    setIsScheduling(true);
    try {
      await apiClient.scheduleFromContentPool(
        items[currentIndex].id,
        useSuggestedTime ? items[currentIndex].suggested_post_time || undefined : undefined
      );
      setStats(prev => ({ ...prev, approved: prev.approved + 1, pending: prev.pending - 1 }));
      goToNext();
    } catch (error) {
      console.error('Failed to schedule:', error);
    } finally {
      setIsScheduling(false);
    }
  };

  const goToNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleGenerate = async () => {
    if (!selectedBrand) return;

    setIsGenerating(true);
    try {
      const themes = generateThemes
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      const response = await apiClient.generateBulkContent({
        brand_id: selectedBrand.id,
        content_count: generateCount,
        platforms: generatePlatforms,
        themes: themes.length > 0 ? themes : undefined,
        use_brand_dna: useBrandDNA,
      });

      if (response.success && response.data) {
        setShowGenerateModal(false);
        await fetchContent();
      }
    } catch (error) {
      console.error('Failed to generate content:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'l') handleApprove();
      else if (e.key === 'ArrowLeft' || e.key === 'h') handleReject();
      else if (e.key === 's') handleSchedule();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, items]);

  const currentItem = items[currentIndex];

  if (!selectedBrand) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center p-6">
          <p className="text-gray-500">Please select a brand from the dashboard</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Content Queue</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Swipe to approve or reject AI-generated content
          </p>
        </div>
        <Button onClick={() => setShowGenerateModal(true)} className="w-full sm:w-auto">
          <Icon name="sparkles" size="sm" className="mr-2" />
          Generate Content
        </Button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="text-center p-4">
          <div className="flex items-center justify-center gap-2 text-yellow-600">
            <Icon name="clock" size="md" />
            <span className="text-2xl font-bold">{stats.pending}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Pending</p>
        </Card>
        <Card className="text-center p-4">
          <div className="flex items-center justify-center gap-2 text-green-600">
            <Icon name="check" size="md" />
            <span className="text-2xl font-bold">{stats.approved}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Approved</p>
        </Card>
        <Card className="text-center p-4">
          <div className="flex items-center justify-center gap-2 text-red-600">
            <Icon name="x" size="md" />
            <span className="text-2xl font-bold">{stats.rejected}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Rejected</p>
        </Card>
      </div>

      {isLoading ? (
        <Card className="text-center py-16">
          <Icon name="sparkles" size="3xl" className="text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-500">Loading content...</p>
        </Card>
      ) : items.length === 0 ? (
        <Card className="text-center py-16">
          <Icon name="sparkles" size="3xl" className="text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No content to review</p>
          <Button onClick={() => setShowGenerateModal(true)}>
            <Icon name="sparkles" size="sm" className="mr-2" />
            Generate Your First Batch
          </Button>
        </Card>
      ) : (
        <>
          {/* Progress */}
          <div className="flex items-center gap-4">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / items.length) * 100}%` }}
              />
            </div>
            <span className="text-sm text-gray-500">
              {currentIndex + 1} / {items.length}
            </span>
          </div>

          {/* Swipe Card */}
          <div className="relative max-w-2xl mx-auto">
            <Card
              className={`transition-all duration-300 transform ${
                swipeDirection === 'right'
                  ? 'translate-x-full opacity-0 rotate-12'
                  : swipeDirection === 'left'
                  ? '-translate-x-full opacity-0 -rotate-12'
                  : ''
              }`}
            >
              {/* Platform badges */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-2">
                  {currentItem?.platforms.map(platform => (
                    <span
                      key={platform}
                      className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize"
                    >
                      {platform}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Quality:</span>
                  <div className="w-16 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${(currentItem?.quality_score || 0) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="bg-gray-50 rounded-lg p-6 mb-4 min-h-[200px]">
                <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {currentItem?.content}
                </p>
              </div>

              {/* Hashtags */}
              {currentItem?.hashtags && currentItem.hashtags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {currentItem.hashtags.map(tag => (
                    <span
                      key={tag}
                      className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-600"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Suggested time */}
              {currentItem?.suggested_post_time && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Icon name="calendar" size="sm" />
                  Suggested: {format(new Date(currentItem.suggested_post_time), 'MMM d, yyyy h:mm a')}
                </div>
              )}
            </Card>

            {/* Swipe Indicators */}
            <div
              className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 pointer-events-none ${
                swipeDirection === 'right' ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div className="bg-green-500 text-white px-6 py-3 rounded-lg text-xl font-bold rotate-12">
                APPROVED
              </div>
            </div>
            <div
              className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 pointer-events-none ${
                swipeDirection === 'left' ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div className="bg-red-500 text-white px-6 py-3 rounded-lg text-xl font-bold -rotate-12">
                REJECTED
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-center gap-4 max-w-2xl mx-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPrevious}
              disabled={currentIndex === 0}
            >
              <Icon name="arrow-left" size="md" />
            </Button>

            <Button
              variant="danger"
              className="w-16 h-16 rounded-full"
              onClick={handleReject}
            >
              <Icon name="thumbs-down" size="lg" />
            </Button>

            <Button
              variant="outline"
              className="w-12 h-12 rounded-full"
              onClick={() => navigate(`/posts/new?content=${encodeURIComponent(currentItem?.content || '')}`)}
            >
              <Icon name="edit" size="md" />
            </Button>

            <Button
              variant="secondary"
              className="w-12 h-12 rounded-full"
              onClick={handleSchedule}
              isLoading={isScheduling}
            >
              <Icon name="calendar" size="md" />
            </Button>

            <Button
              className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600"
              onClick={handleApprove}
            >
              <Icon name="thumbs-up" size="lg" />
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={goToNext}
              disabled={currentIndex === items.length - 1}
            >
              <Icon name="arrow-right" size="md" />
            </Button>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <input
              id="useSuggestedTime"
              type="checkbox"
              checked={useSuggestedTime}
              onChange={e => setUseSuggestedTime(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="useSuggestedTime">
              Schedule at suggested time when available
            </label>
          </div>

          {/* Keyboard hints */}
          <p className="text-center text-sm text-gray-400">
            Keyboard: <kbd className="px-2 py-1 bg-gray-100 rounded">←</kbd> Reject{' '}
            <kbd className="px-2 py-1 bg-gray-100 rounded">→</kbd> Approve{' '}
            <kbd className="px-2 py-1 bg-gray-100 rounded">S</kbd> Schedule
          </p>
        </>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Generate Content</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of posts
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={generateCount}
                  onChange={e => setGenerateCount(parseInt(e.target.value) || 10)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Platforms
                </label>
                <div className="flex flex-wrap gap-2">
                  {(['instagram', 'facebook', 'twitter', 'linkedin', 'tiktok'] as SocialPlatform[]).map(
                    platform => (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => {
                          setGeneratePlatforms(prev =>
                            prev.includes(platform)
                              ? prev.filter(p => p !== platform)
                              : [...prev, platform]
                          );
                        }}
                        className={`px-3 py-1.5 text-sm rounded-lg border-2 capitalize ${
                          generatePlatforms.includes(platform)
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-600'
                        }`}
                      >
                        {platform}
                      </button>
                    )
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Themes (comma-separated, optional)
                </label>
                <input
                  type="text"
                  value={generateThemes}
                  onChange={e => setGenerateThemes(e.target.value)}
                  placeholder="e.g., product launch, tips, behind the scenes"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="useBrandDNA"
                  checked={useBrandDNA}
                  onChange={e => setUseBrandDNA(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="useBrandDNA" className="text-sm text-gray-700">
                  Use Brand DNA for voice consistency
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowGenerateModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={handleGenerate}
                isLoading={isGenerating}
                disabled={generatePlatforms.length === 0}
              >
                <Icon name="sparkles" size="sm" className="mr-2" />
                Generate {generateCount} Posts
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
